import { Chip } from "$components/chip";
import { Table, type TableColumnDefinition } from "$components/table";
import { chipColors } from "$routes/inventory/_lib/constants";
import ExpiryCell from "$routes/inventory/_lib/expiry-cell";
import { ProductOriginBadge } from "$routes/inventory/_lib/product-origin-badge";
import { RHFTextField } from "$routes/inventory/_lib/rhf-text-field";
import { Box } from "@material-ui/core";
import { useIntl } from "react-intl";
import type { FulfillmentSkuData } from "./queries";
import SkuQuantityToSendTotalChip from "./sku-quantity-to-send-total-chip";

function mapFetchedDataToTableData({
  data = [],
  childrenFetched,
  notAvailableMessage,
}: {
  data?: FulfillmentSkuData;
  childrenFetched: boolean;
  notAvailableMessage: string;
}) {
  return data.map((row) => {
    const skuNotAvailableAtLocation =
      childrenFetched && row.availableLots.length === 0;

    return {
      header: {
        id: row.id,
        requestSkuId: row.id,
        skuName: row.sku?.name,
        externalReferences: row.sku?.externalReferences,
        quantityRequested: row.quantityRequested,
        notAvailableAtLocation: skuNotAvailableAtLocation,
        quantityAvailable: skuNotAvailableAtLocation
          ? notAvailableMessage
          : undefined,
      },
      children: row.availableLots?.map((lot) => ({
        id: lot.id,
        requestSkuId: row.id,
        lotId: lot.id,
        quantityAvailable: lot.quantity,
        expiryDate: lot.expiryDate,
      })),
    };
  });
}

type Row = ReturnType<typeof mapFetchedDataToTableData>[0];
type ParentRow = Row["header"];
type ChildRow = Row["children"][0];

interface Props {
  loading?: boolean;
  data: FulfillmentSkuData;
  childrenFetched: boolean;
}

const FulfillMoveRequestTable: FC<Props> = ({
  loading,
  data,
  childrenFetched,
}) => {
  const { formatMessage } = useIntl();

  const columns: TableColumnDefinition[] = [
    {
      field: "skuName",
      headerName: formatMessage({
        id: "inventory.fulfillmoveRequest.table.columnLabel.skuName",
        defaultMessage: "Requested Item",
      }),
      flex: 3,
      renderCell: (value, _index, _column, row: ParentRow, { isParentRow }) => {
        if (!isParentRow) {
          return null;
        }
        return (
          <Box display="flex" alignItems="center" gridGap={8}>
            <span>{value}</span>
            <ProductOriginBadge externalReferences={row.externalReferences} />
          </Box>
        );
      },
    },
    {
      field: "quantityRequested",
      headerName: formatMessage({
        id: "inventory.fulfillmoveRequest.table.columnLabel.quantityRequested",
        defaultMessage: "Qty Requested",
      }),
      flex: 1,

      renderCell: (
        _value,
        _index,
        _column,
        row: ParentRow,
        { isParentRow },
      ) => {
        if (!isParentRow) {
          return null;
        }

        if (!childrenFetched) {
          return (
            <Chip label={row.quantityRequested} color={chipColors.neutral} />
          );
        }

        if (row.notAvailableAtLocation) {
          return (
            <Chip
              label={`0 / ${row.quantityRequested}`}
              color={chipColors.neutral}
            />
          );
        }

        return (
          <SkuQuantityToSendTotalChip
            requestSkuId={row.requestSkuId}
            quantityRequested={row.quantityRequested}
          />
        );
      },
    },
    {
      field: "expiryDate",
      headerName: formatMessage({
        id: "inventory.fulfillmoveRequest.table.columnLabel.expiryDate",
        defaultMessage: "Expiry Date",
      }),
      flex: 1,
      renderCell: (expiryDate) =>
        expiryDate ? <ExpiryCell expiryDate={expiryDate} /> : "",
    },
    {
      field: "quantityAvailable",
      headerName: formatMessage({
        id: "inventory.fulfillmoveRequest.table.columnLabel.quantityAvailable",
        defaultMessage: "Qty Available",
      }),
      flex: 1,
    },
    {
      field: "quantityToSend",
      headerName: formatMessage({
        id: "inventory.fulfillmoveRequest.table.columnLabel.quantityToSend",
        defaultMessage: "Qty To Send",
      }),
      flex: 1,

      renderCell: (_value, _index, _column, row: ChildRow, { isParentRow }) => {
        if (isParentRow) {
          return null;
        }

        return (
          <RHFTextField
            name={`skus.${row.requestSkuId}.${row.lotId}.quantity`}
            inputProps={{ type: "number" }}
            type="number"
            hideHelper
            hiddenLabel
            size="small"
            InputProps={{
              style: { width: "130px", height: "50px", margin: "-8px" },
            }}
          />
        );
      },
    },
  ];

  return (
    <Table
      loading={loading}
      columns={columns}
      rows={mapFetchedDataToTableData({
        data,
        childrenFetched,
        notAvailableMessage: formatMessage({
          id: "inventory.fulfillmoveRequest.table.notAvailableAtLocation",
          defaultMessage: "Not available",
        }),
      })}
    />
  );
};

export default FulfillMoveRequestTable;
